"use client"

import { useEffect, useState, useCallback } from "react"

export type CartLine = {
  itemId: string
  nome: string
  preco: number
  imagem: string
  quantidade: number
  notas?: string
}

export type CustomerProfile = {
  nome: string
  telemovel: string
  email?: string
}

export type SavedOrder = {
  id: string
  data: string
  itens: CartLine[]
  total: number
  horaLevantamento: string
  estado: "recebida" | "a-preparar" | "pronta" | "levantada"
  observacoes?: string
}

const CART_KEY = "ossocios:cart"
const PROFILE_KEY = "ossocios:profile"
const ORDERS_KEY = "ossocios:orders"

function read<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown) {
  if (typeof window === "undefined") return
  window.localStorage.setItem(key, JSON.stringify(value))
  window.dispatchEvent(new CustomEvent("ossocios:storage", { detail: { key } }))
}

export function useCart() {
  const [lines, setLines] = useState<CartLine[]>([])
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setLines(read<CartLine[]>(CART_KEY, []))
    setHydrated(true)
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail || detail.key === CART_KEY) {
        setLines(read<CartLine[]>(CART_KEY, []))
      }
    }
    window.addEventListener("ossocios:storage", handler)
    window.addEventListener("storage", handler)
    return () => {
      window.removeEventListener("ossocios:storage", handler)
      window.removeEventListener("storage", handler)
    }
  }, [])

  const addItem = useCallback(
    (item: { id: string; nome: string; preco: number; imagem: string }) => {
      setLines((prev) => {
        const existing = prev.find((l) => l.itemId === item.id)
        const next = existing
          ? prev.map((l) =>
              l.itemId === item.id ? { ...l, quantidade: l.quantidade + 1 } : l
            )
          : [
              ...prev,
              {
                itemId: item.id,
                nome: item.nome,
                preco: item.preco,
                imagem: item.imagem,
                quantidade: 1,
              },
            ]
        write(CART_KEY, next)
        return next
      })
    },
    []
  )

  const removeItem = useCallback((itemId: string) => {
    setLines((prev) => {
      const next = prev.filter((l) => l.itemId !== itemId)
      write(CART_KEY, next)
      return next
    })
  }, [])

  const updateQty = useCallback((itemId: string, qty: number) => {
    setLines((prev) => {
      const next =
        qty <= 0
          ? prev.filter((l) => l.itemId !== itemId)
          : prev.map((l) => (l.itemId === itemId ? { ...l, quantidade: qty } : l))
      write(CART_KEY, next)
      return next
    })
  }, [])

  const clear = useCallback(() => {
    write(CART_KEY, [])
    setLines([])
  }, [])

  const total = lines.reduce((s, l) => s + l.preco * l.quantidade, 0)
  const totalItens = lines.reduce((s, l) => s + l.quantidade, 0)

  return { lines, addItem, removeItem, updateQty, clear, total, totalItens, hydrated }
}

export function useProfile() {
  const [profile, setProfileState] = useState<CustomerProfile | null>(null)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setProfileState(read<CustomerProfile | null>(PROFILE_KEY, null))
    setHydrated(true)
  }, [])

  const setProfile = useCallback((p: CustomerProfile) => {
    write(PROFILE_KEY, p)
    setProfileState(p)
  }, [])

  return { profile, setProfile, hydrated }
}

export function useOrders() {
  const [orders, setOrders] = useState<SavedOrder[]>([])
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setOrders(read<SavedOrder[]>(ORDERS_KEY, []))
    setHydrated(true)
    const handler = () => setOrders(read<SavedOrder[]>(ORDERS_KEY, []))
    window.addEventListener("ossocios:storage", handler)
    return () => window.removeEventListener("ossocios:storage", handler)
  }, [])

  const addOrder = useCallback((order: SavedOrder) => {
    setOrders((prev) => {
      const next = [order, ...prev].slice(0, 30)
      write(ORDERS_KEY, next)
      return next
    })
  }, [])

  return { orders, addOrder, hydrated }
}

export function formatEUR(n: number): string {
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(n)
}
